import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { INVENTORY_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { trimValues } from "@/composables/util";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";

export const useInventoryStore = defineStore("inventory", () => {
  const configStore = useConfigStore();
  let isDirty = ref(false);
  let pageReference = ref(null);
  let isSearching = ref(false);
  let isSaving = ref(false);
  let structureIdentification = ref({});
  let isAddingOrEditingStructureIdentification = ref(false);
  let isAddingOrEditingStructureComposition = ref(false);
  let isAddingOrEditingFeatures = ref(false);
  let isAddingOrEditingInspectionPlanning = ref(false);
  let isEditingSignAndLight = ref(false);
  let isAddingOrEditingLoadRatingAndPosting = ref(false);
  let isAddingOrEditingPreservationRehab = ref(false);
  let isAddingOrEditingPreservationRehabBearing = ref(false);
  let isAddingOrEditingPreservationRehabContractor = ref(false);
  let isAddingOrEditingPreservationRehabJoint = ref(false);
  let isAddingOrEditingBPR = ref(false);
  let isAddingOrEditingBPAssignment = ref(false);
  let isAddingOrEditingAPRASSpan = ref(false);
  let isAddingOrEditingAPRASSpanClearance = ref(false);
  let isAddingOrEditingAPRASSpanClearanceConditionDetail = ref(false);
  let isAddingOrEditingAPRASPermitCondition = ref(false);
  let isAddingOrEditingMaintenanceTable = ref(false);
  let isAddingOrEditingEDocs = ref(false);
  let isAddingOrEditingDrawingTable = ref(false);
  let isAddingOrEditingValidations = ref(false);
  let isAddingOrEditing = computed(
    () =>
      isAddingOrEditingStructureIdentification.value ||
      isAddingOrEditingStructureComposition.value ||
      isAddingOrEditingFeatures.value ||
      isAddingOrEditingInspectionPlanning.value ||
      isEditingSignAndLight.value ||
      isAddingOrEditingLoadRatingAndPosting.value ||
      isAddingOrEditingPreservationRehab.value ||
      isAddingOrEditingBPR.value ||
      isAddingOrEditingBPAssignment.value ||
      isAddingOrEditingAPRASSpan.value ||
      isAddingOrEditingMaintenanceTable.value ||
      isAddingOrEditingEDocs.value ||
      isAddingOrEditingDrawingTable.value ||
      isAddingOrEditingValidations.value,
  );

  let showValidationSnackbar = ref(false);
  let structureComposition = ref({
    bridgeGeometry: {},
    legacyData: {},
    designInformation: {},
    paints: [],
    structureSpans: [],
    structureSpanSets: [],
    structureSubstructureUnits: [],
    structureSubstructureUnitSets: [],
  });
  let inventoryFeatures = ref({});
  let inspectionPlanning = ref({});
  let preservationRehab = ref({});
  let preservationRehabECMSData = ref({});
  let aprasFields = ref({});
  let aprasRMSRoutes = ref([]);
  let structureUnits = ref([]);
  let structureUnitDropdownValues = ref([]);

  const getIsDirty = () => {
    return isDirty.value;
  };
  const getPageReference = () => {
    return pageReference.value;
  };
  const getIsSearching = () => {
    return isSearching.value;
  };
  const getIsSaving = () => {
    return isSaving.value;
  };
  const getIsAddingPreservationRehabProjectRecordSubTable = () => {
    return (
      isAddingOrEditingPreservationRehabBearing.value ||
      isAddingOrEditingPreservationRehabContractor.value ||
      isAddingOrEditingPreservationRehabJoint.value
    );
  };
  const setIsDirty = (value) => {
    isDirty.value = value;
  };
  const setPageReference = (value) => {
    pageReference.value = value;
  };
  const setIsSearching = (value) => {
    isSearching.value = value;
  };
  const setIsSaving = (value) => {
    isSaving.value = value;
  };
  const resetIsAddingPreservationRehabProjectRecordSubTable = () => {
    isAddingOrEditingPreservationRehabBearing.value = false;
    isAddingOrEditingPreservationRehabContractor.value = false;
    isAddingOrEditingPreservationRehabJoint.value = false;
  };
  const resetAddingOrEditing = () => {
    isAddingOrEditingStructureIdentification.value = false;
    isAddingOrEditingStructureComposition.value = false;
    isAddingOrEditingFeatures.value = false;
    isAddingOrEditingInspectionPlanning.value = false;
    isEditingSignAndLight.value = false;
    isAddingOrEditingLoadRatingAndPosting.value = false;
    isAddingOrEditingPreservationRehab.value = false;
    isAddingOrEditingPreservationRehabBearing.value = false;
    isAddingOrEditingPreservationRehabContractor.value = false;
    isAddingOrEditingPreservationRehabJoint.value = false;
    isAddingOrEditingBPR.value = false;
    isAddingOrEditingBPAssignment.value = false;
    isAddingOrEditingAPRASSpan.value = false;
    isAddingOrEditingAPRASSpanClearance.value = false;
    isAddingOrEditingAPRASSpanClearanceConditionDetail.value = false;
    isAddingOrEditingAPRASPermitCondition.value = false;
    isAddingOrEditingMaintenanceTable.value = false;
    isAddingOrEditingValidations.value = false;
    isAddingOrEditingEDocs.value = false;
    isAddingOrEditingDrawingTable.value = false;
  };
  const getDeptStrucType = (item) => {
    const structureUnit = structureUnits.value.find((c) => {
      return c.strUnitKey === item.strUnitKey;
    });
    if (structureUnit) {
      const deptStrucType = configStore.getDepartmentStructureTypeDescription(
        structureUnit.deptMaterialType +
          structureUnit.deptPhysicalType +
          structureUnit.deptSpanInteraction +
          structureUnit.deptStrucConfig,
      );
      if (deptStrucType) {
        return (
          structureUnit.deptMaterialType +
          structureUnit.deptPhysicalType +
          structureUnit.deptSpanInteraction +
          structureUnit.deptStrucConfig +
          " - " +
          deptStrucType
        );
      }
      return (
        structureUnit.deptMaterialType +
        structureUnit.deptPhysicalType +
        structureUnit.deptSpanInteraction +
        structureUnit.deptStrucConfig
      );
    } else {
      return null;
    }
  };

  const getRiskScoreByBrKey = async (brKey) => {
    let url = INVENTORY_ENDPOINT.GET_RISK_SCORE_BY_BRKEY.replace("{brkey}", brKey);
    isSearching.value = true;
    return await axios
      .get(url)
      .then((response) => {
        isSearching.value = false;
        return response.data.data;
      })
      .catch((e) => {
        isSearching.value = false;
        LOGGER.logException(e);
      });
  };

  const updateRiskAssessmentComment = async (thinRiskScore) => {
    const url = INVENTORY_ENDPOINT.UPDATE_RISK_ASSESSMENT_COMMENT;
    let isSuccess = true;
    await axios
      .put(url, thinRiskScore)
      .then((response) => {
        LOGGER.logEvent(
          LOGGER.EventAction.Update,
          LOGGER.EventCat.RequestResponse,
          "Update RiskAssessmentComment",
          response,
        );
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadInspectionPlanningByBrKey = async (brKey) => {
    let isSuccess = true;
    let url = INVENTORY_ENDPOINT.GET_INSPECTION_PLANNING_BY_BRKEY.replace("{brkey}", brKey);
    isSearching.value = true;
    await axios
      .get(url)
      .then((response) => {
        isSearching.value = false;
        inspectionPlanning.value = response.data.data;
      })
      .catch((e) => {
        isSearching.value = false;
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const updateInspectionPlanning = async (inspectionPlanningData) => {
    const url = INVENTORY_ENDPOINT.UPDATE_INSPECTION_PLANNING;
    let isSuccess = true;
    await axios
      .put(url, inspectionPlanningData)
      .then((response) => {
        LOGGER.logEvent(
          LOGGER.EventAction.Update,
          LOGGER.EventCat.RequestResponse,
          "Update InspectionPlanning",
          response,
        );
        inspectionPlanning.value = response.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadStructureIdentificationByBrKey = async (brKey) => {
    let url = INVENTORY_ENDPOINT.GET_STRUCTURE_IDENTIFICATION_BY_BRKEY.replace("{brkey}", brKey);
    isSearching.value = true;
    return await axios
      .get(url)
      .then((response) => {
        isSearching.value = false;
        structureIdentification.value = trimValues(response.data.data);
      })
      .catch((e) => {
        structureIdentification.value = {};
        isSearching.value = false;
        LOGGER.logException(e);
      });
  };

  const getThinStructureIdentification = async (brkey) => {
    let url = INVENTORY_ENDPOINT.GET_THIN_STRUCTURE_IDENTIFICATION_BY_BRKEY.replace("{brkey}", brkey);
    isSearching.value = true;
    return await axios
      .get(url)
      .then((response) => {
        isSearching.value = false;
        return response.data.data;
      })
      .catch((e) => {
        isSearching.value = false;
        LOGGER.logException(e);
      });
  };

  const updateStructureIdentification = async (identificationData) => {
    let isSuccess = true;
    const url = INVENTORY_ENDPOINT.UPDATE_STRUCTURE_IDENTIFICATION;
    await axios
      .put(url, identificationData)
      .then((response) => {
        LOGGER.logEvent(
          LOGGER.EventAction.Update,
          LOGGER.EventCat.RequestResponse,
          "Update Structure Identification",
          response,
        );
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadStructureCompositionByBrKey = async (brKey) => {
    let url = INVENTORY_ENDPOINT.GET_STRUCTURE_COMPOSITION_BY_BRKEY.replace("{brkey}", brKey);
    isSearching.value = true;
    return await axios
      .get(url)
      .then((response) => {
        isSearching.value = false;
        structureComposition.value = trimValues(response.data.data);
      })
      .catch((e) => {
        isSearching.value = false;
        LOGGER.logException(e);
      });
  };

  const updateStructureComposition = async (compositionData) => {
    let isSuccess = true;
    const url = INVENTORY_ENDPOINT.UPDATE_STRUCTURE_COMPOSITION;
    await axios
      .put(url, compositionData)
      .then((response) => {
        LOGGER.logEvent(
          LOGGER.EventAction.Update,
          LOGGER.EventCat.RequestResponse,
          "Update Structure Composition",
          response,
        );
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadFeaturesByBrKey = async (brKey) => {
    let isSuccess = true;
    let url = INVENTORY_ENDPOINT.GET_FEATURES_BY_BRKEY.replace("{brkey}", brKey);
    isSearching.value = true;
    await axios
      .get(url)
      .then((response) => {
        isSearching.value = false;
        inventoryFeatures.value = response.data.data;
      })
      .catch((e) => {
        isSearching.value = false;
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const updateFeatures = async (featuresData) => {
    let isSuccess = true;
    const url = INVENTORY_ENDPOINT.UPDATE_FEATURES;
    await axios
      .put(url, featuresData)
      .then((response) => {
        LOGGER.logEvent(
          LOGGER.EventAction.Update,
          LOGGER.EventCat.RequestResponse,
          "Update Inventory Features",
          response,
        );
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadPreservationAndRehabECMSData = async (ecmsNo) => {
    let isSuccess = false;
    let url = INVENTORY_ENDPOINT.GET_PRESERVATION_AND_REHAB_ECMS_DATA.replace("{ecmsNo}", ecmsNo);
    isSearching.value = true;
    preservationRehabECMSData.value = null;
    await axios
      .get(url)
      .then((resp) => {
        isSearching.value = false;
        isSuccess = true;
        preservationRehabECMSData.value = resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSearching.value = false;
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadPreservationRehabAndByBrKey = async (brKey) => {
    let isSuccess = false;
    let url = INVENTORY_ENDPOINT.GET_PRESERVATION_AND_REHAB.replace("{brkey}", brKey);
    isSearching.value = true;
    await axios
      .get(url)
      .then((resp) => {
        isSearching.value = false;
        isSuccess = true;
        preservationRehab.value = resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSearching.value = false;
        isSuccess = false;
      });
    return isSuccess;
  };

  const updatePreservationAndRehab = async (item) => {
    let isSuccess = false;
    let url = INVENTORY_ENDPOINT.UPDATE_PRESERVATION_AND_REHAB;
    isSearching.value = true;
    await axios
      .put(url, item)
      .then((resp) => {
        isSearching.value = false;
        isSuccess = true;
        preservationRehab.value = resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSearching.value = false;
        isSuccess = false;
      });
    return isSuccess;
  };

  const validateWorkPerfromed = async (prId, wtId, workPerformed) => {
    let url = INVENTORY_ENDPOINT.VALIDATE_PRESERVATION_AND_REHAB_WORK_PERFORMED;
    return await axios
      .post(url, workPerformed, {
        params: { prId: prId, wtId: wtId },
      })
      .then((resp) => {
        return resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const loadAprasByBrKey = async (brKey) => {
    let isSuccess = false;
    let url = INVENTORY_ENDPOINT.GET_APRAS.replace("{brkey}", brKey);
    isSearching.value = true;
    await axios
      .get(url)
      .then((resp) => {
        isSearching.value = false;
        isSuccess = true;
        aprasFields.value = resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSearching.value = false;
        isSuccess = false;
      });
    return isSuccess;
  };

  const loadStructureUnits = async (brKey) => {
    structureUnitDropdownValues.value = [];
    structureUnits.value = [];
    let url = INVENTORY_ENDPOINT.GET_STRUCTURE_UNITS.replace("{brkey}", brKey);
    await axios
      .get(url)
      .then((resp) => {
        for (const value of resp.data.data ?? []) {
          structureUnits.value.push(value);
          structureUnitDropdownValues.value.push({ title: value.strUnitLabel, value: value.strUnitKey });
        }
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const loadAprasRMSRoutes = async (brKey) => {
    aprasRMSRoutes.value = [];
    let url = INVENTORY_ENDPOINT.GET_COMBINED_RMS.replace("{brkey}", brKey);
    await axios
      .get(url)
      .then((resp) => {
        for (const value of resp.data.data ?? []) {
          aprasRMSRoutes.value.push({
            title:
              value.paCounty +
              " " +
              value.srNum +
              " " +
              value.segNum +
              " " +
              value.offset +
              " " +
              value.roadwayName?.trim() +
              " - " +
              configStore.getReferenceValue(REFERENCE_TABLE.ON_UNDER, value.onUnder),
            value: value.onUnder,
            featureKey: value.featureKey,
          });
        }
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const updateApras = async (item) => {
    let isSuccess = false;
    let url = INVENTORY_ENDPOINT.UPDATE_APRAS;
    isSearching.value = true;
    await axios
      .put(url, item)
      .then((resp) => {
        isSearching.value = false;
        isSuccess = true;
        aprasFields.value = resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSearching.value = false;
        isSuccess = false;
      });
    return isSuccess;
  };

  const isValidAARDotNum = async (aarDotNum) => {
    let isValid = true;
    let url = INVENTORY_ENDPOINT.VALIDATE_CROSSING_ID.replace("{aarDotNum}", aarDotNum);
    await axios.get(url).catch((e) => {
      isValid = false;
      LOGGER.logException(e);
    });
    return isValid;
  };

  const getCrossingDetails = async (aarDotNum) => {
    let url = INVENTORY_ENDPOINT.GET_CROSSING_DETAILS.replace("{aarDotNum}", aarDotNum);
    isSearching.value = true;
    return await axios
      .get(url)
      .then((resp) => {
        isSearching.value = false;
        return resp.data.data;
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSearching.value = false;
      });
  };

  return {
    isAddingOrEditingMaintenanceTable,
    structureUnitDropdownValues,
    loadStructureUnits,
    structureUnits,
    getDeptStrucType,
    aprasRMSRoutes,
    loadAprasRMSRoutes,
    isAddingOrEditingAPRASPermitCondition,
    isAddingOrEditingAPRASSpanClearanceConditionDetail,
    isAddingOrEditingAPRASSpanClearance,
    isAddingOrEditingAPRASSpan,
    aprasFields,
    updateApras,
    loadAprasByBrKey,
    isAddingOrEditingBPAssignment,
    resetAddingOrEditing,
    isAddingOrEditingBPR,
    updatePreservationAndRehab,
    validateWorkPerfromed,
    isAddingOrEditingInspectionPlanning,
    updateInspectionPlanning,
    loadInspectionPlanningByBrKey,
    inspectionPlanning,
    updateRiskAssessmentComment,
    getRiskScoreByBrKey,
    getIsSaving,
    setIsSaving,
    setIsDirty,
    getIsDirty,
    setPageReference,
    getPageReference,
    getIsSearching,
    setIsSearching,
    loadStructureIdentificationByBrKey,
    structureIdentification,
    updateStructureIdentification,
    isAddingOrEditingStructureIdentification,
    getThinStructureIdentification,
    showValidationSnackbar,
    isAddingOrEditingStructureComposition,
    loadStructureCompositionByBrKey,
    structureComposition,
    updateStructureComposition,
    isAddingOrEditingFeatures,
    isEditingSignAndLight,
    isAddingOrEditingLoadRatingAndPosting,
    loadFeaturesByBrKey,
    updateFeatures,
    inventoryFeatures,
    isAddingOrEditingPreservationRehab,
    loadPreservationAndRehabECMSData,
    loadPreservationRehabAndByBrKey,
    preservationRehabECMSData,
    preservationRehab,
    isValidAARDotNum,
    getCrossingDetails,
    isAddingOrEditingPreservationRehabJoint,
    isAddingOrEditingPreservationRehabContractor,
    isAddingOrEditingPreservationRehabBearing,
    getIsAddingPreservationRehabProjectRecordSubTable,
    resetIsAddingPreservationRehabProjectRecordSubTable,
    isAddingOrEditing,
    isAddingOrEditingEDocs,
    isAddingOrEditingDrawingTable,
    isAddingOrEditingValidations,
  };
});
